import React, { useEffect } from "react";
import styled, { css } from "styled-components";
import { useFormikContext } from "formik";
import { Trans, useTranslation } from "react-i18next";
import i18n from "i18n";
import { Alert, Col, ControlLabel, FormGroup } from "components/bootstrap";
import { FormikFormGroup, FormikInput, Link, Spinner } from "components/common";
import useSnapshotArchivingDefaults from "archive/hooks/useSnapshotArchivingDefaults";
import useSnapshotArchivingTypes from "archive/hooks/useSnapshotArchivingTypes";
import useDataTiering from "data-tiering/hooks/useDataTiering";
import DataTieringRoutes from "data-tiering/Routes";
import RepositoryManagement from "data-tiering/RepositoryManagement";
import WarmTierReadinessInfo from "data-tiering/WarmTierReadinessInfo";
import RepositoryAccessInfo from "data-tiering/repository-management/RepositoryAccessInfo";
import SnapshotFSPathFieldGroup from "./SnapshotFSPathFieldGroup";
const FIELD_PREFIX = "snapshot_archiving_config";
const TYPES_WITHOUT_SPOOLING = ["fs-1", "cloud-1"];
export const DEFAULT_SNAPSHOT_CONFIG = {
  type: null,
  repository_name: null,
  leader_only: true,
  spooling_directory: null
};
const StyledAlert = styled(Alert)(
  () => css`
    margin-top: 0;
  `
);
export const validateSnapshotArchivingFields = (config) => {
  const errors = {};
  const tValidation = (key) => i18n.t(`snapshot_archiving_fields.validation.${key}`, { ns: "enterprise_archive" });
  if (!(config == null ? void 0 : config.repository_name)) {
    errors.repository_name = tValidation("select_repository");
  }
  if ((config == null ? void 0 : config.repository_name) && !(config == null ? void 0 : config.type)) {
    errors.repository_name = tValidation("no_type");
  }
  if (config && config.type !== "cloud-1" && config.spooling_directory && !config.spooling_directory.startsWith("/")) {
    errors.spooling_directory = tValidation("absolute_path");
  }
  return errors;
};
const SpoolingDirectoryFieldGroup = ({ placeholder }) => {
  const { t } = useTranslation("enterprise_archive");
  const { setFieldValue } = useFormikContext();
  useEffect(
    () => () => {
      setFieldValue(`${FIELD_PREFIX}.spooling_directory`, void 0);
    },
    [setFieldValue]
  );
  return /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: `${FIELD_PREFIX}.spooling_directory`,
      type: "text",
      label: t("snapshot_archiving_fields.spooling_directory_label"),
      placeholder,
      help: t("snapshot_archiving_fields.spooling_directory_help"),
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  );
};
const SnapshotArchivingFields = ({
  repositories,
  credentialProblemRepositories,
  hasSupportedRepositories,
  isLoadingRepositories = false,
  disabled = false
}) => {
  var _a, _b, _c, _d, _e;
  const { t } = useTranslation("enterprise_archive");
  const { values, errors, setFieldValue } = useFormikContext();
  const { readiness } = useDataTiering();
  const { data: snapshotTypes, isLoading: isLoadingTypes } = useSnapshotArchivingTypes();
  const { data: snapshotDefaults } = useSnapshotArchivingDefaults();
  const snapshotConfig = values.snapshot_archiving_config;
  const selectedRepoName = (_a = snapshotConfig == null ? void 0 : snapshotConfig.repository_name) != null ? _a : null;
  const supportedRepositoryTypes = (snapshotTypes != null ? snapshotTypes : []).map((t2) => t2.repository_type);
  const selectedRepository = (_b = repositories.find((entry) => entry.repository.name === selectedRepoName)) != null ? _b : null;
  const derivedType = selectedRepository ? (_d = (_c = snapshotTypes == null ? void 0 : snapshotTypes.find((t2) => t2.repository_type === selectedRepository.repository.type)) == null ? void 0 : _c.type) != null ? _d : null : null;
  useEffect(() => {
    if (snapshotConfig && derivedType && snapshotConfig.type !== derivedType) {
      setFieldValue(`${FIELD_PREFIX}.type`, derivedType);
    }
  }, [snapshotConfig, derivedType, setFieldValue]);
  const selectedNotReady = (selectedRepository == null ? void 0 : selectedRepository.status) === "not_ready";
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(WarmTierReadinessInfo, null)), ((_e = readiness.data) == null ? void 0 : _e.ready) && (isLoadingTypes || isLoadingRepositories ? /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(Spinner, { text: t("snapshot_archiving_fields.loading_types") })) : /* @__PURE__ */ React.createElement(React.Fragment, null, typeof errors.snapshot_archiving_config === "string" && /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger" }, errors.snapshot_archiving_config)), !hasSupportedRepositories && /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger", title: t("snapshot_archiving_fields.no_supported_title") }, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "snapshot_archiving_fields.no_supported_text",
      components: { warmTierLink: /* @__PURE__ */ React.createElement(Link, { to: DataTieringRoutes.WARM_TIER_REPOSITORIES }) }
    }
  ))), hasSupportedRepositories && credentialProblemRepositories.length > 0 && /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger", title: t("snapshot_archiving_fields.no_credentials_title") }, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "snapshot_archiving_fields.no_credentials_text",
      values: { repositories: credentialProblemRepositories.join(", ") },
      components: { warmTierLink: /* @__PURE__ */ React.createElement(Link, { to: DataTieringRoutes.WARM_TIER_REPOSITORIES }) }
    }
  ))), hasSupportedRepositories && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(RepositoryAccessInfo, null)), /* @__PURE__ */ React.createElement(
    RepositoryManagement,
    {
      layout: "horizontal",
      disabled,
      fieldName: `${FIELD_PREFIX}.repository_name`,
      types: supportedRepositoryTypes,
      label: t("snapshot_archiving_fields.snapshot_repository_label"),
      help: t("snapshot_archiving_fields.snapshot_repository_help")
    }
  ), selectedNotReady && /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "danger" }, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "snapshot_archiving_fields.selected_not_ready",
      components: { warmTierLink: /* @__PURE__ */ React.createElement(Link, { to: DataTieringRoutes.WARM_TIER_REPOSITORIES }) }
    }
  ))), derivedType === "fs-1" && /* @__PURE__ */ React.createElement(SnapshotFSPathFieldGroup, null), derivedType && !TYPES_WITHOUT_SPOOLING.includes(derivedType) && /* @__PURE__ */ React.createElement(SpoolingDirectoryFieldGroup, { placeholder: snapshotDefaults == null ? void 0 : snapshotDefaults.spooling_directory }), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, t("snapshot_archiving_fields.restrict_leader_label")), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      name: `${FIELD_PREFIX}.leader_only`,
      id: "snapshot_leader_only",
      type: "checkbox",
      formGroupClassName: "col-sm-9",
      label: t("snapshot_archiving_fields.restrict_leader_checkbox"),
      help: t("snapshot_archiving_fields.restrict_leader_help")
    }
  )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, t("snapshot_archiving_fields.processing_threads_label")), /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      name: `${FIELD_PREFIX}.processing_threads`,
      id: "snapshot_processing_threads",
      type: "number",
      min: 1,
      placeholder: t("snapshot_archiving_fields.automatic"),
      formGroupClassName: "col-sm-9",
      label: t("snapshot_archiving_fields.threads_one_shard_label"),
      help: t("snapshot_archiving_fields.threads_help")
    }
  ))))));
};
export default SnapshotArchivingFields;
